import { namesTechnical } from './namemode.ts';
import { humanize } from './format.ts';

// The derivation rules are also in cloud/src/nouns.ts (the Cloud names a class with them the first time it sees it):
// keep the two in sync — lib/nouns.test.ts runs both on one table of examples (cloud/test/noun-examples.ts).

/**
 * Words that name a system rather than the thing: dropped from the front of a data model's name
 * (ErpSalesOrder → sales orders, FinvoiceInvoice → invoices) — but MeterReading keeps its "meter".
 */
const SYSTEM_WORDS = new Set([
  'web', 'webshop', 'shop', 'erp', 'wms', 'crm', 'edi', 'dwh', 'hr', 'pay', 'payroll', 'fin', 'finvoice',
  'car', 'carrier', 'ecb', 'fac', 'esg', 'maventa', 'posti', 'salesforce', 'dynamics', 'shopify',
]);

/** The plural of a name said in the singular: sales order → sales orders, delivery → deliveries; a plural stays. */
export function pluralOf(base: string): string {
  if (/[^su]s$/.test(base)) return base;
  if (/[^aeiou]y$/.test(base)) return base.slice(0, -1) + 'ies';
  if (/(s|x|ch|sh)$/.test(base)) return base + 'es';
  return base + 's';
}

/** The name derived from the class name alone, whatever the catalog says: ErpSalesOrder → sales order(s). */
export function derivedNoun(cls: string, n = 1): string {
  const words = humanize(cls).toLowerCase().split(' ').filter(Boolean);
  const rest = words.length > 1 && SYSTEM_WORDS.has(words[0]) ? words.slice(1) : words;
  const base = rest.join(' ');
  // A class named in the plural already (reference rates) stays as it is
  return n === 1 ? base : pluralOf(base);
}

// ---------------------------------------------------------------- the catalog's names

/** Display names from the data model catalog (lib/catalog.tsx fills it), by class: [singular, plural]. */
let names = new Map<string, readonly [string, string]>();

/** Replaces the names the catalog gives (every class the catalog has). */
export function setCatalogNames(entries: Iterable<{ class: string; displayName: string; displayPlural: string }>): void {
  const next = new Map<string, readonly [string, string]>();
  for (const e of entries) if (e.displayName) next.set(e.class, [e.displayName, e.displayPlural || pluralOf(e.displayName)] as const);
  names = next;
}

/** Changes the names of one class (after an edit). */
export function setCatalogName(cls: string, displayName: string, displayPlural: string): void {
  names = new Map(names).set(cls, [displayName, displayPlural || pluralOf(displayName)] as const);
}

/**
 * A data model as people would say it: the catalog's display name (n = 1) or plural (otherwise) when it has one,
 * else derived from the class name: ErpSalesOrder → sales order(s), EcbReferenceRates → reference rates.
 */
export function noun(cls: string, n = 1): string {
  if (namesTechnical()) return cls;
  const named = names.get(cls);
  if (named) return n === 1 ? named[0] : named[1];
  return derivedNoun(cls, n);
}
